import React, { useEffect, useState } from "react";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FieldArray, useFormik } from "formik";
import ProductItemServices from "../../../services/OrderingServices/ProductItemService";
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Button,
  Card,
  CardBody,
  Col,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
  Table,
} from "reactstrap";
import * as Yup from "yup";
import Select from "react-select";
import CategoryServices from "../../../services/OrderingServices/CategoryService";
import UnitService from "../../../services/OrderingServices/UnitService";
import toast from "react-hot-toast";
import ItemService from "../../../services/OrderingServices/ItemService";
// import FileDropzone from "../../../CosmosAdminPages/ResuableComponent/DropzoneComponent";

const validationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  description: Yup.string(),
  price: Yup.number()
    .min(0, "Price cannot be negative")
    .required("Price is required"),
  tags: Yup.string(),
  images: Yup.string(),
  categoryId: Yup.number().min(1, "Please select a category"),
  unitId: Yup.number()
    .min(1, "Please select a unit")
    .required("Unit is required"),
  // isMenuDisplay: Yup.boolean(),
  isHidden: Yup.boolean(),
  isProductSoldOut: Yup.boolean(),
  attributes: Yup.array().of(
    Yup.object({
      title: Yup.string(),
      value: Yup.number().min(0, "Attribute value cannot be negative"),
      isActive: Yup.boolean(),
    })
  ),
});

const ItemForm = () => {
  const breadcrumbItems = [{ title: "Back To List", link: "/menu-item" }];
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categoryData, setCategoryData] = useState([]);
  const [unitList, setUnitList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [open, setOpen] = useState("0"); // Manage which accordion item is open
  const [selectedFiles, setSelectedFiles] = useState([]);
  const toggle = (id) => {
    setOpen(open === id ? "" : id); // Toggle the accordion item
  };
  const navigate = useNavigate();
  const fetchCategory = async () => {
    try {
      const response = await CategoryServices.maincategory();
      setCategoryData(response);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchUnit = async () => {
    try {
      setLoading(true);
      const response = await UnitService.getList();
      setUnitList(response);
    } catch (error) {
      console.log("🚀 ~ error:", error);
    } finally {
      setLoading(false);
    }
  };
  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await ItemService.itemList();
      setProductList(response);
    } catch (error) {
      console.log("🚀 ~ error:", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchCategory();
    fetchUnit();
    fetchProduct();
  }, []);

  const [initialData, setInitialData] = useState([]);
  const fetchItemDetail = async () => {
    try {
      const response = await ItemService.getDetail(id);
      console.log("🚀 ~ fetchItemDetail ~ response:", response);
      setInitialData(response);
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    fetchItemDetail();
  }, [id]);
  const formik = useFormik({
    initialValues: {
      id: initialData?.id,
      name: initialData?.name || "",
      slug: initialData?.slug || "",
      description: initialData?.description || "",
      price: initialData?.price || 0,
      tags: initialData?.tags || "",
      image: initialData?.image || "",
      categoryId: initialData?.categoryId || "",
      parentId: initialData?.parentId || null,
      unitId: initialData?.unitId || null,
      isHidden: initialData?.isHidden || false,
      isProductSoldOut: initialData?.isProductSoldOut || false,
      attributes: initialData?.attributes || [],
      children: initialData?.children || [],
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      try {
        setIsSubmitting(true);

        // Create FormData object for multipart/form-data
        const formData = new FormData();

        // Add basic form fields
        formData.append("id", values.id);

        formData.append("name", values.name);
        formData.append("slug", values.slug || "");
        formData.append("description", values.description);
        formData.append("price", values.price.toString());
        formData.append("tags", values.tags);
        formData.append("isHidden", values.isHidden.toString());
        formData.append("isProductSoldOut", values.isProductSoldOut.toString());

        // Add optional fields only if they have values
        if (values.categoryId) {
          formData.append("categoryId", values.categoryId.toString());
        }

        if (values.unitId) {
          formData.append("unitId", values.unitId.toString());
        }

        if (values.parentId) {
          formData.append("parentId", values.parentId.toString());
        }

        // ✅ handle image
        if (values.image instanceof File) {
          formData.append("image", values.image);
        } else if (values.existingImage) {
          formData.append("existingImage", values.existingImage);
        }

        // Handle attributes array - filter out empty titles
        const filteredAttributes = values.attributes.filter(
          (attr) => attr.title.trim() !== ""
        );

        if (filteredAttributes.length > 0) {
          // Option 1: Send as JSON string
          // formData.append('attributes', JSON.stringify(filteredAttributes));

          // Option 2: Send each attribute separately (if your backend expects this format)
          filteredAttributes.forEach((attr, index) => {
            formData.append(`attributes[${index}][id]`, attr.id.toString());
            formData.append(`attributes[${index}][title]`, attr.title);
            formData.append(
              `attributes[${index}][value]`,
              attr.value.toString()
            );
            formData.append(
              `attributes[${index}][isActive]`,
              attr.isActive.toString()
            );
          });
        }

        // Handle children array
        if (values.children && values.children.length > 0) {
          // Send each child separately with indexed format
          values.children.forEach((child, index) => {
            formData.append(`children[${index}][id]`, child.id.toString());
            if (child.childProductId) {
              formData.append(
                `children[${index}][childProductId]`,
                child.childProductId.toString()
              );
            }
            formData.append(`children[${index}][name]`, child.name || "");
            formData.append(
              `children[${index}][price]`,
              child.price?.toString() || "0"
            );
            formData.append(
              `children[${index}][description]`,
              child.description || ""
            );
            formData.append(
              `children[${index}][choices]`,
              child.choices?.toString() || "0"
            );
          });
        }

        // Debug: Log FormData contents
        console.log("FormData contents:");
        for (let pair of formData.entries()) {
          console.log(pair[0] + ": " + pair[1]);
        }

        if (id) {
          await ProductItemServices.update(id, formData);
          navigate("/menu-item");
          toast.success("Product updated successfully!");
        } else {
          await ProductItemServices.create(formData);
          resetForm();
          navigate("/menu-item");
          toast.success("Product created successfully!");
        }
      } catch (error) {
        console.error("Submit Error:", error);
        toast.error("Failed to save product. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  console.log("🚀 ~ ItemForm ~ formik:", formik);
  const handleAddChildrenAttribute = () => {
    const newParentItem = {
      id: 0,
      childProductId: null,
      name: "",
      price: null,
      description: "",
      choices: null,
    };
    const updatedParentItems = [...formik.values.children, newParentItem];
    formik.setFieldValue("children", updatedParentItems);
  };

  const handleRemoveParent = (index) => {
    const updatedItems = [...formik.values.children];
    updatedItems.splice(index, 1);
    formik.setFieldValue("children", updatedItems);
  };
  const handleAddAttribute = () => {
    const newAttributeItem = {
      id: 0,
      title: "",
      value: 0,
      isActive: true,
    };
    const updatedAttributeItems = [
      ...formik.values.attributes,
      newAttributeItem,
    ];
    formik.setFieldValue("attributes", updatedAttributeItems);
  };

  const handleRemoveAttribute = (index) => {
    const updatedItems = [...formik.values.attributes];
    updatedItems.splice(index, 1);
    formik.setFieldValue("attributes", updatedItems);
  };

  const handleCategoryChange = (selectedOption) => {
    formik.setFieldValue(
      "categoryId",
      selectedOption ? selectedOption.value : null
    );
  };

  const handleUnitChange = (selectedOption) => {
    formik.setFieldValue(
      "unitId",
      selectedOption ? selectedOption.value : null
    );
  };
  const handleParentChange = (selectedOption) => {
    console.log("🚀 ~ handleParentChange ~ selectedOption:", selectedOption);
    formik.setFieldValue(
      "parentId",
      selectedOption ? selectedOption.value : null
    );
  };
  const categoryOptions =
    categoryData?.items?.map((type) => ({
      value: type.id,
      label: type.name,
    })) || [];

  const selectedCategory = categoryOptions.find(
    (option) => option.value === formik.values.categoryId
  );

  const unitOptions =
    unitList?.map((type) => ({
      value: type.id,
      label: type.name,
    })) || [];

  const selectedUnit = unitOptions.find(
    (option) => option.value === formik.values.unitId
  );
  const parentOptions = productList?.items?.map((p) => ({
    value: p.id,
    label: p.name,
    price: p.price,
    description: p.description,
    choices: p.choices,
  }));

  return (
    <div className="page-content">
      <div className="container-fluid">
        <BreadCrumb
          pageTitle="Product"
          title="Product Detail"
          breadcrumbItems={breadcrumbItems}
        />

        <form onSubmit={formik.handleSubmit}>
          <Row>
            <Col md={6}>
              <Card>
                <CardBody>
                  <Row>
                    <Col lg={12}>
                      <div className="form-floating mb-3">
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          className="form-control"
                          placeholder="Enter product name"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.name}
                          invalid={
                            !!(formik.touched.name && formik.errors.name)
                          }
                        />
                        <Label for="name">Name</Label>
                        <FormFeedback>{formik.errors.name}</FormFeedback>
                      </div>
                    </Col>

                    <Col lg={6}>
                      <div className="form-floating mb-3">
                        <Input
                          id="price"
                          name="price"
                          type="number"
                          className="form-control"
                          placeholder="Enter product price"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.price}
                          invalid={
                            !!(formik.touched.price && formik.errors.price)
                          }
                        />
                        <Label for="price">Price</Label>
                        <FormFeedback>{formik.errors.price}</FormFeedback>
                      </div>
                    </Col>
                    <Col lg={6}>
                      <div className="form-floating mb-3">
                        <Input
                          id="tags"
                          name="tags"
                          type="text"
                          className="form-control"
                          placeholder="Enter product tags"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.tags}
                          invalid={
                            !!(formik.touched.tags && formik.errors.tags)
                          }
                        />
                        <Label for="tags">Tags</Label>
                        <FormFeedback>{formik.errors.tags}</FormFeedback>
                      </div>
                    </Col>
                    <Col lg={6}>
                      <div className="mb-3">
                        <Select
                          id="categoryId"
                          name="categoryId"
                          options={categoryOptions}
                          value={selectedCategory || null}
                          onChange={handleCategoryChange}
                          placeholder="Select Category"
                          isClearable
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            control: (baseStyles) => ({
                              ...baseStyles,
                              padding: "0.3rem",
                            }),
                          }}
                        />
                      </div>
                    </Col>
                    <Col lg={6}>
                      <div className="mb-3">
                        <Select
                          id="unitId"
                          name="unitId"
                          options={unitOptions}
                          value={selectedUnit || null}
                          onChange={handleUnitChange}
                          placeholder="Select Unit"
                          isClearable
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            control: (baseStyles) => ({
                              ...baseStyles,
                              padding: "0.3rem",
                            }),
                          }}
                        />
                        {formik.touched.unitId && formik.errors.unitId && (
                          <div className="text-danger">
                            {formik.errors.unitId}
                          </div>
                        )}
                      </div>
                    </Col>

                    <Col md={12} className="mb-3">
                      <label htmlFor="description">Description</label>
                      <textarea
                        id="description"
                        name="description"
                        className={`form-control ${
                          formik.touched.description &&
                          formik.errors.description
                            ? "is-invalid"
                            : ""
                        }`}
                        placeholder="Enter description"
                        rows={4}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.description}
                        style={{ padding: "1rem" }}
                      />
                      {formik.touched.description &&
                        formik.errors.description && (
                          <div className="invalid-feedback">
                            {formik.errors.description}
                          </div>
                        )}
                    </Col>
                    <Col lg={6}>
                      <FormGroup check>
                        <Label check>
                          <Input
                            type="checkbox"
                            name="isHidden"
                            onChange={formik.handleChange}
                            checked={formik.values.isHidden}
                          />{" "}
                          Shouldn't display in menu
                        </Label>
                      </FormGroup>
                    </Col>
                    <Col lg={6}>
                      <FormGroup check>
                        <Label check>
                          <Input
                            type="checkbox"
                            name="isProductSoldOut"
                            onChange={formik.handleChange}
                            checked={formik.values.isProductSoldOut}
                          />{" "}
                          Sold Out
                        </Label>
                      </FormGroup>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>

            <Col md={6} className="mb-3">
              <Card className="h-100">
                <CardBody>
                  <Row>
                    <Col xs={12}>
                      <div className="d-flex justify-content-between mt-2 mb-2">
                        <div>
                          <h5>Children item</h5>
                        </div>
                        <Button
                          color="success"
                          size="sm"
                          type="button"
                          onClick={handleAddChildrenAttribute}
                        >
                          <i className="bx bx-plus"></i> Add children
                        </Button>
                      </div>
                      <Accordion open={open} toggle={toggle}>
                        {formik.values.children &&
                        formik.values.children.length > 0 ? (
                          formik.values.children.map((item, index) => {
                            const selectedParent = parentOptions?.find(
                              (opt) => opt.value === item.childProductId
                            );

                            return (
                              <AccordionItem key={index}>
                                <AccordionHeader targetId={index.toString()}>
                                  <div className="d-flex justify-content-between  w-100 align-content-center align-items-center">
                                    <h6>{item.name || "Unnamed Child"}</h6>
                                    <div className="me-2">
                                      <Button
                                        color="light"
                                        size="sm"
                                        type="button"
                                        onClick={() =>
                                          handleRemoveParent(index)
                                        }
                                      >
                                        <i className="ri-delete-bin-line"></i>
                                      </Button>
                                    </div>
                                  </div>
                                </AccordionHeader>
                                <AccordionBody accordionId={index.toString()}>
                                  <Row className="mb-2">
                                    <Col md={6}>
                                      <label>Name</label>
                                      <Select
                                        name={`children[${index}].childProductId`}
                                        options={parentOptions}
                                        value={selectedParent || null}
                                        onChange={(selected) => {
                                          if (selected) {
                                            formik.setFieldValue(
                                              `children[${index}].childProductId`,
                                              selected.value
                                            );
                                            formik.setFieldValue(
                                              `children[${index}].name`,
                                              selected.label
                                            );
                                            formik.setFieldValue(
                                              `children[${index}].price`,
                                              selected.price || 0
                                            );
                                            formik.setFieldValue(
                                              `children[${index}].description`,
                                              selected.description || ""
                                            );
                                            formik.setFieldValue(
                                              `children[${index}].choices`,
                                              selected.choices || 0
                                            );
                                          } else {
                                            formik.setFieldValue(
                                              `children[${index}].childProductId`,
                                              null
                                            );
                                            formik.setFieldValue(
                                              `children[${index}].name`,
                                              ""
                                            );
                                            formik.setFieldValue(
                                              `children[${index}].price`,
                                              0
                                            );
                                            formik.setFieldValue(
                                              `children[${index}].description`,
                                              ""
                                            );
                                            formik.setFieldValue(
                                              `children[${index}].choices`,
                                              0
                                            );
                                          }
                                        }}
                                        isClearable
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <label>Price</label>
                                      <Input
                                        name={`children[${index}].price`}
                                        type="number"
                                        value={item.price}
                                        onChange={formik.handleChange}
                                        placeholder="Price"
                                        invalid={
                                          !!(
                                            formik.touched.children?.[index]
                                              ?.price &&
                                            formik.errors.children?.[index]
                                              ?.price
                                          )
                                        }
                                      />
                                      <FormFeedback>
                                        {formik.errors.children?.[index]?.price}
                                      </FormFeedback>
                                    </Col>
                                  </Row>
                                  <Row className="mb-2">
                                    <Col md={6}>
                                      <label>Choices</label>
                                      <Input
                                        name={`children[${index}].choices`}
                                        type="number"
                                        value={item.choices}
                                        onChange={formik.handleChange}
                                        placeholder="Choices"
                                        invalid={
                                          !!(
                                            formik.touched.children?.[index]
                                              ?.choices &&
                                            formik.errors.children?.[index]
                                              ?.choices
                                          )
                                        }
                                      />
                                      <FormFeedback>
                                        {
                                          formik.errors.children?.[index]
                                            ?.choices
                                        }
                                      </FormFeedback>
                                    </Col>
                                  </Row>
                                  <Row className="mb-2">
                                    <Col md={12}>
                                      <label>Description</label>
                                      <Input
                                        name={`children[${index}].description`}
                                        type="textarea"
                                        rows={4}
                                        value={item.description}
                                        onChange={formik.handleChange}
                                        placeholder="Description"
                                        invalid={
                                          !!(
                                            formik.touched.children?.[index]
                                              ?.description &&
                                            formik.errors.children?.[index]
                                              ?.description
                                          )
                                        }
                                      />
                                      <FormFeedback>
                                        {
                                          formik.errors.children?.[index]
                                            ?.description
                                        }
                                      </FormFeedback>
                                    </Col>
                                  </Row>
                                </AccordionBody>
                              </AccordionItem>
                            );
                          })
                        ) : (
                          <div className="text-center p-3">
                            Please add an attribute to view them here.
                          </div>
                        )}
                      </Accordion>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col lg={4}>
              <div className="mb-2">
                
              </div>
            </Col>
            <Col lg={8}>
              <Card className="pt-2">
                <CardBody>
                  <Row>
                    <Col xs={12}>
                      <div className="d-flex justify-content-between mt-2 mb-2">
                        <div>
                          <h5>Attributes</h5>
                        </div>
                        <Button
                          color="success"
                          size="sm"
                          type="button"
                          onClick={handleAddAttribute}
                        >
                          <i className="bx bx-plus"></i> Add Attribute
                        </Button>
                      </div>
                      <Table bordered>
                        <thead>
                          <tr>
                            <th>S.N</th>
                            <th>Title</th>
                            <th>Price</th>
                            <th>Is Active</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formik.values.attributes &&
                          formik.values.attributes.length > 0 ? (
                            formik.values.attributes.map((item, index) => (
                              <tr key={index}>
                                <td>{index + 1}</td>
                                <td>
                                  <Input
                                    name={`attributes[${index}].title`}
                                    value={item.title}
                                    onChange={formik.handleChange}
                                    placeholder="Attribute Title"
                                    invalid={
                                      !!(
                                        formik.touched.attributes?.[index]
                                          ?.title &&
                                        formik.errors.attributes?.[index]?.title
                                      )
                                    }
                                  />
                                  <FormFeedback>
                                    {formik.errors.attributes?.[index]?.title}
                                  </FormFeedback>
                                </td>
                                <td>
                                  <Input
                                    name={`attributes[${index}].value`}
                                    type="number"
                                    value={item.value}
                                    onChange={formik.handleChange}
                                    placeholder="Price"
                                    invalid={
                                      !!(
                                        formik.touched.attributes?.[index]
                                          ?.value &&
                                        formik.errors.attributes?.[index]?.value
                                      )
                                    }
                                  />
                                  <FormFeedback>
                                    {formik.errors.attributes?.[index]?.value}
                                  </FormFeedback>
                                </td>
                                <td>
                                  <Input
                                    type="checkbox"
                                    name={`attributes[${index}].isActive`}
                                    checked={item.isActive}
                                    onChange={formik.handleChange}
                                  />
                                </td>
                                <td>
                                  <Button
                                    color="danger"
                                    size="sm"
                                    type="button"
                                    onClick={() => handleRemoveAttribute(index)}
                                  >
                                    <i className="bx bx-minus"></i>
                                  </Button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan="5"
                                className="text-center p-lg-4 p-2"
                              >
                                Please add an attribute to view them here.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <div className="mt-4 d-flex gap-3 justify-content-end">
            <Link to="/menu-item">
              <Button color="danger">Cancel</Button>
            </Link>
            <Button type="submit" color="success" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : id ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ItemForm;
